import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, LogIn } from "lucide-react";
import { Link } from 'react-router-dom';
import { TEST_IDS } from "@/lib/testIds";
import { waLink } from "@/lib/contact";
import { Logo } from "./Logo";

const links = [
  { id: "anasayfa", label: "Anasayfa", key: "home" },
  { id: "hakkimda", label: "Hakkımda", key: "about" },
  { id: "sertifikalar", label: "Sertifikalar", key: "certificates" },
  { id: "hizmetler", label: "Hizmetler", key: "services" },
  { id: "iletisim", label: "İletişim", key: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color,box-shadow] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-[#0F2D2A]/10 shadow-[0_8px_30px_-20px_rgba(15,45,42,0.25)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* ROW 1 — Logo (left) + Neon Randevu Al (right) */}
        <div className="h-14 md:h-16 flex items-center justify-between gap-3">
          <a
            href="#anasayfa"
            data-testid="nav-brand-home"
            onClick={(e) => handleClick(e, "anasayfa")}
            className="flex items-center shrink-0"
          >
            <span className="hidden sm:inline-flex">
              <Logo variant="full" size={34} />
            </span>
            <span className="sm:hidden inline-flex">
              <Logo variant="mark" size={30} />
            </span>
          </a>

          <div className="flex items-center gap-3 sm:gap-5">
          <Link to="/giris" data-testid="nav-login-link" className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-bold text-[#0F2D2A] hover:text-[#047857] transition-colors whitespace-nowrap"><LogIn size={15} />Giriş Yap</Link>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={TEST_IDS.nav.appointment}
            className="neon-pulse inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#10B981] via-[#0AB7C5] to-[#0EA5E9] text-white px-4 py-2 sm:px-5 sm:py-2.5 md:px-6 md:py-3 rounded-full text-[10px] sm:text-[11px] md:text-xs font-extrabold uppercase tracking-[0.14em] md:tracking-[0.18em] border border-white/35 hover:-translate-y-0.5 transition-transform duration-300 shrink-0"
          >
            <span className="hidden sm:inline">Randevu Al</span>
            <span className="sm:hidden">Randevu</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
          </div>
        </div>

        {/* ROW 2 — Menu, centered, distributed, breathable */}
        <nav
          className={`flex items-center justify-between sm:justify-center gap-2 sm:gap-8 md:gap-12 lg:gap-16 h-11 md:h-12 border-t transition-colors duration-500 ${
            scrolled ? "border-[#0F2D2A]/10" : "border-[#0F2D2A]/15"
          }`}
        >
          {links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              onClick={(e) => handleClick(e, l.id)}
              data-testid={TEST_IDS.nav[l.key]}
              className="text-[9px] sm:text-xs md:text-[13px] font-extrabold text-[#0F2D2A] uppercase tracking-normal sm:tracking-[0.16em] md:tracking-[0.2em] whitespace-nowrap link-underline"
            >
              {l.label}
            </a>
          ))}
        </nav>
      </div>
    </motion.header>
  );
};

export default Navbar;
